import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { syncNow } from '@/lib/sync';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import { useRows } from '@/hooks/useLive';
import { humanise } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusChip } from '@/components/ui/StatusChip';
import { CheckboxInput, TextInput } from '@/components/ui/Field';
import { OPERATIONS, operationLabel, operationsOf } from '@/lib/operations';
import type { Site } from '@/types/models';

/**
 * Branches (0055). Administrators add, rename, remove and restore them.
 *
 * Removing a branch that was ever used deactivates it: it leaves every
 * pick-list and new-record form, while its history stays and still reports.
 * One that was never used is deleted. The quarry cannot be removed. The
 * database decides which (remove_site); this screen shows what it decided.
 */
type SiteRow = Site & Record<string, unknown> & { timezone?: string };

const EMPTY = { name: '', code: '', timezone: 'Africa/Windhoek', operations: ['concrete'] as string[] };

/** Plain words for the tables site_usage() counts. */
const USAGE_LABEL: Record<string, string> = {
  concrete_deliveries: 'concrete deliveries',
  concrete_targets: 'concrete targets',
  fuel_issues: 'fuel fills',
  fuel_receipts: 'fuel deliveries',
  fuel_dips: 'tank dips',
  fuel_tanks: 'tanks',
  machines: 'vehicles',
  daily_diary: 'diaries',
  breakdowns: 'breakdowns',
  vehicle_checks: 'vehicle checks',
  job_cards: 'job cards',
  profiles: 'users',
  machine_transfers: 'vehicle moves',
  report_recipients: 'report recipients',
  site_settings: 'branch settings',
};

function usageText(usage: Record<string, number>): string {
  return Object.entries(usage)
    .filter(([table]) => table !== 'user_sites')
    .map(([table, n]) => `${n} ${USAGE_LABEL[table] ?? humanise(table)}`)
    .join(', ');
}

export function BranchesPage() {
  const isAdmin = useAuthStore((s) => s.profile?.role === 'admin');
  const sites = useRows<SiteRow>('sites', 'name');
  const rows = (sites ?? []).filter((s) => !s.deleted_at && (s.kind === 'branch' || s.kind === 'quarry'));

  const [editing, setEditing] = useState<SiteRow | 'new' | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState<Record<string, number> | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const current = editing && editing !== 'new' ? editing : null;
  const isQuarry = current?.kind === 'quarry';

  async function open(row: SiteRow | 'new') {
    setEditing(row);
    setConfirmRemove(false);
    setUsage(null);
    setForm(row === 'new'
      ? EMPTY
      : { name: row.name, code: row.code, timezone: row.timezone ?? 'Africa/Windhoek', operations: operationsOf(row) });
    if (row !== 'new' && row.kind === 'branch') {
      const { data } = await supabase.rpc('site_usage', { p_site: row.id });
      setUsage((data ?? {}) as Record<string, number>);
    }
  }

  async function run(what: string, fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
      await syncNow();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? `Could not ${what}`);
    } finally {
      setBusy(false);
    }
  }

  const onSave = () => run('save the branch', async () => {
    if (editing === 'new') {
      const { error } = await supabase.rpc('create_site', {
        p_name: form.name, p_code: form.code, p_timezone: form.timezone, p_operations: form.operations,
      });
      if (error) throw error;
      toast.success(`${form.name.trim()} added. Give people access under Users & roles.`);
    } else if (current) {
      const { error } = await supabase.rpc('update_site', {
        p_site: current.id, p_name: form.name, p_code: form.code, p_timezone: form.timezone,
        p_operations: form.operations,
      });
      if (error) throw error;
      toast.success(`${form.name.trim()} saved`);
    }
    setEditing(null);
  });

  const onRemove = () => run('remove the branch', async () => {
    if (!current) return;
    const { data, error } = await supabase.rpc('remove_site', { p_site: current.id });
    if (error) throw error;
    const result = data as { result: 'deleted' | 'deactivated'; name: string };
    toast.success(result.result === 'deleted'
      ? `${result.name} deleted`
      : `${result.name} deactivated — its history is kept`);
    setEditing(null);
  });

  const onRestore = () => run('restore the branch', async () => {
    if (!current) return;
    const { error } = await supabase.rpc('restore_site', { p_site: current.id });
    if (error) throw error;
    toast.success(`${current.name} is active again`);
    setEditing(null);
  });

  const columns: Column<SiteRow>[] = [
    { key: 'name', label: 'Name' },
    { key: 'code', label: 'Code' },
    {
      key: 'kind', label: 'Runs',
      value: (s) => [s.kind === 'quarry' ? 'Quarry' : '', ...operationsOf(s).map(operationLabel)].filter(Boolean).join(', '),
      render: (s) => {
        const ops = [s.kind === 'quarry' ? 'Quarry' : '', ...operationsOf(s).map(operationLabel)].filter(Boolean);
        return ops.length ? ops.join(' · ') : <span className="muted">Nothing ticked</span>;
      },
    },
    {
      key: 'is_active', label: 'Status', align: 'center',
      value: (s) => (s.is_active ? 'active' : 'inactive'),
      render: (s) => (s.is_active
        ? <StatusChip tone="green">Active</StatusChip>
        : <StatusChip tone="grey">Deactivated</StatusChip>),
    },
  ];

  const used = usage !== null && Object.keys(usage).some((t) => t !== 'user_sites');

  return (
    <>
      <Panel
        title="Branches"
        flush
        actions={isAdmin ? <Button onClick={() => void open('new')}>+ Add branch</Button> : undefined}
      >
        <div style={{ padding: 'var(--sp-3) var(--sp-3) 0' }} className="small muted">
          {isAdmin
            ? <>The quarry and the branches, with what each runs (Concrete, Brick Plant, Pre-cast). A new
                branch is visible only to users given it (or All branches) under Users &amp; roles.</>
            : <>Only an administrator can add, rename or remove a branch.</>}
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(s) => s.id}
          onRowClick={isAdmin ? (s) => void open(s) : undefined}
          defaultSort="name"
          searchPlaceholder="Search branches…"
          empty="No branches."
        />
      </Panel>

      <Drawer
        open={editing !== null}
        title={editing === 'new' ? 'Add branch' : 'Edit branch'}
        subtitle={current?.name}
        onClose={() => setEditing(null)}
        footer={
          <>
            <span className="spacer" />
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button onClick={onSave} loading={busy}>{editing === 'new' ? 'Add branch' : 'Save'}</Button>
          </>
        }
      >
        <TextInput id="b-name" label="Name" required
          value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <TextInput id="b-code" label="Code" required
          value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })}
          help="A short code used on reports and lists, e.g. WVB." />
        <div className="form-section">Runs</div>
        <p className="small muted" style={{ marginTop: 0 }}>
          {isQuarry
            ? 'The quarry: diary, production, blasting and targets. It may also run a brick plant or pre-cast yard.'
            : 'Every branch has its fleet, vehicle checks, breakdowns and fuel. Tick what else it runs: only ticked '
              + 'branches show in that module and its figures. Unticking keeps the history.'}
        </p>
        {/* 0069: Brick Plant and Pre-cast are linked to branches again (and may run at the quarry). */}
        {OPERATIONS.filter((o) => !(isQuarry && o.key === 'concrete')).map((o) => (
          <CheckboxInput key={o.key} id={`b-op-${o.key}`} label={o.label} help={o.help}
            value={form.operations.includes(o.key)}
            onChange={(on) => setForm({
              ...form,
              operations: on ? [...form.operations.filter((x) => x !== o.key), o.key] : form.operations.filter((x) => x !== o.key),
            })} />
        ))}
        <TextInput id="b-tz" label="Time zone"
          value={form.timezone} onChange={(v) => setForm({ ...form, timezone: v })}
          help="Africa/Windhoek unless the branch is elsewhere." />

        {current && !isQuarry && (
          <>
            <div className="form-section">{current.is_active ? 'Remove' : 'Deactivated'}</div>
            {!current.is_active ? (
              <>
                <p className="small muted" style={{ marginTop: 0 }}>
                  This branch is deactivated: it is not offered on any form, and its history
                  still shows in reports.
                </p>
                <Button variant="secondary" onClick={onRestore} loading={busy}>Restore branch</Button>
              </>
            ) : (
              <>
                <p className="small muted" style={{ marginTop: 0 }}>
                  {usage === null ? 'Checking what this branch holds…'
                    : used
                      ? <>This branch holds {usageText(usage)}. Removing it deactivates it: it leaves
                          every form and pick-list, and its history stays and still reports.
                          It can be restored later.</>
                      : <>Nothing has been recorded at this branch, so removing it deletes it.</>}
                </p>
                {confirmRemove ? (
                  <div className="row" style={{ gap: 'var(--sp-2)' }}>
                    <Button variant="danger" onClick={onRemove} loading={busy}>
                      Yes, {used ? 'deactivate' : 'delete'} {current.name}
                    </Button>
                    <Button variant="secondary" onClick={() => setConfirmRemove(false)} disabled={busy}>
                      Keep
                    </Button>
                  </div>
                ) : (
                  <Button variant="secondary" onClick={() => setConfirmRemove(true)}
                    disabled={busy || usage === null}>
                    Remove branch
                  </Button>
                )}
              </>
            )}
          </>
        )}
        {isQuarry && (
          <p className="small muted">The quarry can be renamed but not removed.</p>
        )}
      </Drawer>
    </>
  );
}
